// EditDocumentScreen.js — version complète (RTE + Liens + Docs optionnels + Échantillons + Feuilletage)
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
  ActivityIndicator,
  Platform,
  SafeAreaView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { ensureEpreuveId } from '../services/documentIdentity';
import DocumentLifecycleService from '../services/DocumentLifecycleService';
import AdminLayout from './web-ui/AdminLayout';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

/* ----------------------------- convertFirebaseURL ------------------------- */
const convertFirebaseURL = (url) => {
  if (!url) return null;
  // Convertir .firebasestorage.app en .appspot.com
  return url.replace(
    /firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\.firebasestorage\.app/,
    'firebasestorage.googleapis.com/v0/b/$1.appspot.com'
  );
};

// ⚠️ Fonctionnalité "restriction de téléchargement" SUSPENDUE.
// Passer à true pour réafficher l'option dans le formulaire d'édition.
const RESTRICTION_FEATURE_ENABLED = false;

/* --------------------------------- helpers --------------------------------- */
const moveItem = (arr, from, to) => {
  if (!Array.isArray(arr)) return arr;
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [it] = copy.splice(from, 1);
  copy.splice(to, 0, it);
  return copy;
};

const safeId = () => (globalThis.crypto?.randomUUID?.() || Date.now().toString(36));

const getFileName = (item) => {
  if (!item) return '';
  if (typeof item === 'string') {
    try {
      const u = new URL(item);
      return decodeURIComponent(u.pathname.split('/').pop() || item);
    } catch {
      return item;
    }
  }
  return item.name || item.file?.name || 'Fichier';
};

// Fonction pour calculer la réduction (compatible avec la version Android)
const calculateReduction = (prix, reductionPourcent) => {
  const prixNum = parseFloat(prix) || 0;
  const reductionNum = parseFloat(reductionPourcent) || 0;

  if (prixNum <= 0 || reductionNum <= 0 || reductionNum > 100) {
    return {
      prixInitial: prix,
      reductionPourcent: '0',
      prixFinal: prix
    };
  }

  const montantReduction = (prixNum * reductionNum) / 100;
  const prixFinal = prixNum - montantReduction;

  return {
    prixInitial: prix,
    reductionPourcent: reductionPourcent,
    prixFinal: prixFinal.toString()
  };
};

/* ----------------------------- Rich Text Editor ---------------------------- */
const RichTextEditor = ({ value, onChange, placeholder, style, theme }) => {
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const applyFormat = (format) => {
    const { start, end } = selection;
    const selectedText = value?.substring(start, end) || '';
    let formattedText = '';

    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      case 'list':
        formattedText = selectedText
          ? selectedText.split('\n').map((l) => (l.trim() ? `- ${l}` : l)).join('\n')
          : '- ';
        break;
      default:
        return;
    }

    const newValue =
      (value || '').substring(0, start) + formattedText + (value || '').substring(end);
    onChange(newValue);
  };

  return (
    <View style={style}>
      <View style={[styles.formattingToolbar, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat('bold')}>
          <MaterialCommunityIcons name="format-bold" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat('italic')}>
          <MaterialCommunityIcons name="format-italic" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat('underline')}>
          <MaterialCommunityIcons name="format-underline" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat('list')}>
          <MaterialCommunityIcons name="format-list-bulleted" size={16} color={theme.text} />
        </TouchableOpacity>
      </View>

      <TextInput
        style={[
          styles.input,
          styles.textArea,
          { backgroundColor: theme.background, color: theme.text, borderColor: theme.border },
        ]}
        placeholder={placeholder}
        value={value}
        onChangeText={onChange}
        onSelectionChange={(e) => {
          setSelection({
            start: e.nativeEvent.selection.start,
            end: e.nativeEvent.selection.end,
          });
        }}
        multiline
        numberOfLines={4}
        placeholderTextColor={theme.textSecondary}
      />
      <Text style={[styles.formattingHint, { color: theme.textSecondary }]}>
        Sélectionnez du texte puis appliquez un style (Markdown léger).
      </Text>
    </View>
  );
};

/* ----------------------------- Component Screen ---------------------------- */
export default function EditDocumentScreen({ route, navigation }) {
  const { document } = route.params || {};
  const { theme: baseTheme } = useTheme();
  // Sur le web (dashboard), on surcharge le theme pour matcher le style admin.
  const isWeb = typeof window !== 'undefined' && !!window.document;
  const theme = isWeb
    ? {
        ...baseTheme,
        background: '#f7f9fc',
        surface: '#ffffff',
        border: '#e4e8ef',
        primary: '#165ca2',
        primaryDark: '#0e4079',
        text: '#1a2236',
        textSecondary: '#5d6781',
        success: '#10b981',
        danger: '#ef4444',
      }
    : baseTheme;

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formValid, setFormValid] = useState(false);

  const [formData, setFormData] = useState(() => initializeFormData(document));

  function initializeFormData(docSnap) {
    if (!docSnap || !docSnap.type) {
      // Document invalide, retourner une structure vide par défaut
      return {
        titre: '',
        description: '',
      };
    }

    switch (docSnap.type) {
      case 'concours':
        return {
          sigle: docSnap.sigle || '',
          nomComplet: docSnap.nomComplet || '',
          description: docSnap.description || '',
          niveau: docSnap.niveau || '',
          langue: docSnap.langue || 'fr',
          typeConcours: docSnap.typeConcours || '',
          coverImage: docSnap.coverImage || null,
          hasGroups: !!docSnap.hasGroups,
          groups: Array.isArray(docSnap.groups) ? docSnap.groups : [],
          arrete: docSnap.arrete || { titre: '', annee: '', description: '', document: null },
          arretes: Array.isArray(docSnap.arretes)
            ? docSnap.arretes.map((a) => ({
                id: a.id || safeId(),
                titre: a.titre || '',
                annee: a.annee || new Date().getFullYear(),
                document: a.document || null,
                url: a.url || '',
              }))
            : [],
          documents: !docSnap.hasGroups && Array.isArray(docSnap.documents) ? docSnap.documents : [],
          restrictionTelechargement: docSnap.restrictionTelechargement || false,
        };
      case 'bords':
        return {
          titre: docSnap.titre || '',
          description: docSnap.description || '',
          image: docSnap.image || null,
          documents: Array.isArray(docSnap.documents) ? docSnap.documents : [], // liste de PDF principaux
          documentsAnglais: Array.isArray(docSnap.documentsAnglais) ? docSnap.documentsAnglais : [], // version anglaise
          documentsOptionnels: Array.isArray(docSnap.documentsOptionnels) ? docSnap.documentsOptionnels : [],
          liens: Array.isArray(docSnap.liens) ? docSnap.liens : [],
          prix: docSnap.prix || '',
          nombrePages: docSnap.nombrePages || '',
          reductionPourcent: docSnap.reductionPourcent || '',
          prixInitial: docSnap.prixInitial || '',
          prixFinal: docSnap.prixFinal || '',
          echantillons: Array.isArray(docSnap.echantillons) ? docSnap.echantillons : [],
          langue: docSnap.langue || 'fr',
          feuilletage: docSnap.feuilletage || null,
          restrictionTelechargement: docSnap.restrictionTelechargement || false,
        };
      case 'simple':
        return {
          titre: docSnap.titre || '',
          description: docSnap.description || '',
          document: docSnap.document || null, // string URL ou asset
          coverImage: docSnap.coverImage || null,
          liens: Array.isArray(docSnap.liens) ? docSnap.liens : [],
          restrictionTelechargement: docSnap.restrictionTelechargement || false,
        };
      default:
        return {};
    }
  }

  /* ------------------------------- validations ------------------------------ */
  useEffect(() => {
    if (!document || !document.type) {
      setFormValid(false);
      return;
    }

    let ok = false;
    switch (document.type) {
      case 'concours':
        ok = validateConcoursForm();
        break;
      case 'bords':
        ok = validateBordsForm();
        break;
      case 'simple':
        ok = validateSimpleForm();
        break;
      default:
        ok = false;
    }
    setFormValid(ok);
  }, [formData, document.type]);

  const validateConcoursForm = () => {
    const {
      sigle,
      nomComplet,
      description,
      niveau,
      langue,
      typeConcours,
      hasGroups,
      groups,
      documents,
      arretes,
    } = formData;

    const arretesValid = (arretes || []).every(
      (a) => a.titre?.trim() && a.annee?.toString().trim() && (a.document || a.url?.trim())
    );

    const docsValid = hasGroups
      ? (groups || []).every(
          (g) =>
            g.nom?.trim() &&
            (g.documents || []).every(
              (d) =>
                d.titre?.trim() &&
                d.filiere?.trim() &&
                d.annee?.toString().trim() &&
                d.nombrePages?.toString().trim() &&
                d.prix?.toString().trim() &&
                (d.document || d.url)
            )
        )
      : (documents || []).every(
          (d) =>
            d.titre?.trim() &&
            d.filiere?.trim() &&
            d.annee?.toString().trim() &&
            d.nombrePages?.toString().trim() &&
            d.prix?.toString().trim() &&
            (d.document || d.url)
        );

    const hasDocs = hasGroups
      ? (groups || []).some((g) => (g.documents || []).length > 0)
      : (documents || []).length > 0;

    return Boolean(
      sigle?.trim() &&
        nomComplet?.trim() &&
        description?.trim() &&
        niveau?.trim() &&
        langue &&
        typeConcours?.trim() &&
        hasDocs &&
        docsValid &&
        arretesValid
    );
  };

  // Assouplie: le(s) PDF(s) principal(aux) NE SONT PAS obligatoires pour l'édition
  const validateBordsForm = () => {
    const { titre, description, image, prix, nombrePages, langue } = formData;
    return Boolean(
      titre?.trim() &&
        description?.trim() &&
        image &&
        prix?.toString().trim() &&
        nombrePages?.toString().trim() &&
        langue
    );
  };

  // Assouplie: soit un document, soit au moins 1 lien
  const validateSimpleForm = () => {
    const { titre, description, document, liens } = formData;
    return Boolean(
      titre?.trim() && description?.trim() && (document || (Array.isArray(liens) && liens.length > 0))
    );
  };

  /* ------------------------------ Pickers media ----------------------------- */
  const pickImage = async (type, forEchantillon = false) => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        quality: 0.85,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const uri = result.assets[0].uri;

      if (type === 'bords') {
        if (forEchantillon) {
          setFormData((p) => ({ ...p, echantillons: [...(p.echantillons || []), uri] }));
        } else {
          setFormData((p) => ({ ...p, image: uri }));
        }
      } else if (type === 'concours') {
        setFormData((p) => ({ ...p, coverImage: uri }));
      } else if (type === 'simple') {
        setFormData((p) => ({ ...p, coverImage: uri }));
      }
    } catch (e) {
      Platform.OS === 'web'
        ? alert("Erreur : Impossible de sélectionner l'image")
        : Alert.alert('Erreur', "Impossible de sélectionner l'image");
    }
  };

  const pickPDF = async () => {
    const res = await DocumentPicker.getDocumentAsync({
      type: 'application/pdf',
      multiple: false,
      copyToCacheDirectory: true,
    });
    return res.canceled || !res.assets?.[0] ? null : res.assets[0];
  };

  // Concours: ajouter un doc (sans groupes)
  const handlePickConcoursDocument = async () => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({
        ...p,
        documents: [
          ...(p.documents || []),
          {
            document: file,
            titre: '',
            filiere: '',
            annee: new Date().getFullYear(),
            nombrePages: '',
            prix: '',
            gratuit: false,
          },
        ],
      }));
    } catch {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document');
    }
  };

  // Bords: ajouter un doc principal
  const handlePickBordsDocument = async () => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({ ...p, documents: [...(p.documents || []), file] }));
    } catch {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document');
    }
  };

  // Arrêté: choisir PDF
  const handlePickDocumentForArrete = async (arreteId) => {
    try {
      const file = await pickPDF();
      if (!file) return;
      updateArrete(arreteId, 'document', file);
    } catch (error) {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document');
    }
  };

  const handlePickFeuilletage = async () => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({ ...p, feuilletage: file }));
    } catch (error) {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document de feuilletage')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document de feuilletage');
    }
  };

  // Bords: document optionnel
  const pickDocumentOptionnel = async () => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({
        ...p,
        // Ajout EN HAUT — demande utilisateur
        documentsOptionnels: [
          { id: safeId(), document: file, titre: '', description: '', prix: '' },
          ...(p.documentsOptionnels || []),
        ],
      }));
    } catch {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document');
    }
  };

  const removeDocumentOptionnel = (id) => {
    setFormData((p) => ({
      ...p,
      documentsOptionnels: (p.documentsOptionnels || []).filter((d) => d.id !== id),
    }));
  };

  const updateDocumentOptionnel = (id, field, value) => {
    setFormData((p) => ({
      ...p,
      documentsOptionnels: (p.documentsOptionnels || []).map((d) =>
        d.id === id ? { ...d, [field]: value } : d
      ),
    }));
  };

  /* ------------------------------- Liens (CRUD) ------------------------------ */
  const addLien = (target) => {
    const item = { id: safeId(), titre: '', url: '', description: '' };
    if (target === 'bords') {
      setFormData((p) => ({ ...p, liens: [...(p.liens || []), item] }));
    } else if (target === 'simple') {
      setFormData((p) => ({ ...p, liens: [...(p.liens || []), item] }));
    }
  };

  const removeLien = (target, id) => {
    if (target === 'bords') {
      setFormData((p) => ({ ...p, liens: (p.liens || []).filter((l) => l.id !== id) }));
    } else if (target === 'simple') {
      setFormData((p) => ({ ...p, liens: (p.liens || []).filter((l) => l.id !== id) }));
    }
  };

  const updateLien = (target, id, field, value) => {
    if (target === 'bords') {
      setFormData((p) => ({
        ...p,
        liens: (p.liens || []).map((l) => (l.id === id ? { ...l, [field]: value } : l)),
      }));
    } else if (target === 'simple') {
      setFormData((p) => ({
        ...p,
        liens: (p.liens || []).map((l) => (l.id === id ? { ...l, [field]: value } : l)),
      }));
    }
  };

  /* ------------------------------ CRUD Arrêtés ------------------------------ */
  const addArrete = () =>
    setFormData((p) => ({
      ...p,
      arretes: [
        ...(p.arretes || []),
        { id: safeId(), titre: '', annee: new Date().getFullYear(), document: null, url: '' },
      ],
    }));

  const removeArrete = (id) =>
    setFormData((p) => ({ ...p, arretes: (p.arretes || []).filter((a) => a.id !== id) }));

  const updateArrete = (id, field, value) =>
    setFormData((p) => ({
      ...p,
      arretes: (p.arretes || []).map((a) => (a.id === id ? { ...a, [field]: value } : a)),
    }));

  /* --------------------------- Groupes (concours) --------------------------- */
  const addGroup = () =>
    setFormData((p) => ({
      ...p,
      groups: [...(p.groups || []), { id: safeId(), nom: '', documents: [] }],
    }));

  // Supprimer un groupe retire TOUTES ses épreuves d'un coup : on vérifie
  // qu'aucune n'a été vendue, sinon les acheteurs perdraient leur accès.
  const removeGroup = async (id) => {
    const group = (formData.groups || []).find((g) => g.id === id);
    for (const epreuve of group?.documents || []) {
      const check = await DocumentLifecycleService.checkEpreuveRemoval(document.id, epreuve);
      if (!check.canRemove) {
        window.alert(
          `Suppression bloquée\n\nLe groupe contient une épreuve vendue ("${epreuve?.titre || 'sans titre'}"). ${check.message}`
        );
        return;
      }
    }
    setFormData((p) => ({ ...p, groups: (p.groups || []).filter((g) => g.id !== id) }));
  };

  const updateGroup = (id, field, value) =>
    setFormData((p) => ({
      ...p,
      groups: (p.groups || []).map((g) => (g.id === id ? { ...g, [field]: value } : g)),
    }));

  const addDocumentToGroup = async (groupId) => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({
        ...p,
        groups: (p.groups || []).map((g) =>
          g.id === groupId
            ? {
                ...g,
                // Ajout EN HAUT du groupe — demande utilisateur
                documents: [
                  {
                    document: file,
                    titre: '',
                    filiere: '',
                    annee: new Date().getFullYear(),
                    nombrePages: '',
                    prix: '',
                    gratuit: false,
                  },
                  ...(g.documents || []),
                ],
              }
            : g
        ),
      }));
    } catch {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de sélectionner le document')
        : Alert.alert('Erreur', 'Impossible de sélectionner le document');
    }
  };

  // Remplace le fichier PDF d'un document EXISTANT dans un groupe (édition).
  const replaceDocumentFileInGroup = async (groupId, documentIndex) => {
    try {
      const file = await pickPDF();
      if (!file) return;
      setFormData((p) => ({
        ...p,
        groups: (p.groups || []).map((g) =>
          g.id === groupId
            ? {
                ...g,
                documents: (g.documents || []).map((doc, idx) =>
                  idx === documentIndex ? { ...doc, document: file } : doc
                ),
              }
            : g
        ),
      }));
    } catch {
      Platform.OS === 'web'
        ? alert('Erreur : Impossible de remplacer le fichier')
        : Alert.alert('Erreur', 'Impossible de remplacer le fichier');
    }
  };

  // Une épreuve déjà vendue ne peut pas être retirée : les acheteurs
  // perdraient l'accès à un contenu payé. Remplacer le PDF reste possible.
  const removeDocumentFromGroup = async (groupId, documentIndex) => {
    const group = (formData.groups || []).find((g) => g.id === groupId);
    const epreuve = group?.documents?.[documentIndex];
    const check = await DocumentLifecycleService.checkEpreuveRemoval(document.id, epreuve);
    if (!check.canRemove) {
      window.alert(`Suppression bloquée\n\n${check.message}`);
      return;
    }
    setFormData((p) => ({
      ...p,
      groups: (p.groups || []).map((g) =>
        g.id === groupId
          ? { ...g, documents: (g.documents || []).filter((_, i) => i !== documentIndex) }
          : g
      ),
    }));
  };

  // Retrait d'un document de bord.
  // Un bord vendu doit toujours conserver au moins un document : sinon les
  // acheteurs se retrouvent avec un contenu vide. Remplacer reste possible.
  const removeBordDocument = async (index) => {
    const restant = (formData.documents || []).length - 1;
    const check = await DocumentLifecycleService.checkBordDocumentRemoval(document.id, restant);
    if (!check.canRemove) {
      window.alert(`Suppression bloquée\n\n${check.message}`);
      return;
    }
    setFormData((p) => ({ ...p, documents: (p.documents || []).filter((_, x) => x !== index) }));
  };

  // Même protection pour les concours sans groupes (liste plate).
  const removeDocumentFromFlatList = async (index) => {
    const epreuve = formData.documents?.[index];
    const check = await DocumentLifecycleService.checkEpreuveRemoval(document.id, epreuve);
    if (!check.canRemove) {
      window.alert(`Suppression bloquée\n\n${check.message}`);
      return;
    }
    setFormData((p) => ({ ...p, documents: (p.documents || []).filter((_, i) => i !== index) }));
  };

  const updateDocumentInGroup = (groupId, documentIndex, field, value) =>
    setFormData((p) => ({
      ...p,
      groups: (p.groups || []).map((g) =>
        g.id === groupId
          ? {
              ...g,
              documents: (g.documents || []).map((d, i) =>
                i === documentIndex ? { ...d, [field]: value } : d
              ),
            }
          : g
      ),
    }));

  /* ------------------------------ Upload helpers --------------------------- */
  const uploadImage = async (uri, type) => {
    if (!uri || typeof uri !== 'string' || uri.startsWith('http')) return uri;
    const response = await fetch(uri);
    const blob = await response.blob();
    const storage = getStorage();
    const fileName = `images/${type}/${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const storageRef = ref(storage, fileName);
    const task = uploadBytesResumable(storageRef, blob);
    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (s) => setUploadProgress((s.bytesTransferred / s.totalBytes) * 100),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });
  };

  const uploadDocument = async (uri, type, needsRestriction = false) => {
    if (!uri || typeof uri !== 'string' || uri.startsWith('http')) return uri;
    const response = await fetch(uri);
    const blob = await response.blob();
    const storage = getStorage();
    const fileName = `documents/${type}/${Date.now()}_${Math.random().toString(36).slice(2)}.pdf`;
    const storageRef = ref(storage, fileName);
    const task = uploadBytesResumable(storageRef, blob);
    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (s) => setUploadProgress((s.bytesTransferred / s.totalBytes) * 100),
        reject,
        async () => {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          // ⚠️ Fonctionnalité "restriction téléchargement" SUSPENDUE.
          // La conversion .firebasestorage.app -> .appspot.com produisait des URLs
          // cassées (404). On conserve l'URL native retournée par Firebase.
          resolve(downloadURL);
        }
      );
    });
  };

  /* --------------------------------- Submit -------------------------------- */
  const handleSubmit = async () => {
    if (!formValid || !document || !document.type || !document.id) {
      Alert.alert('Erreur', 'Document invalide');
      return;
    }
    try {
      setLoading(true);
      setUploadProgress(0);

      let updatedData = { ...formData };
      const collectionName = {
        concours: 'concours',
        bords: 'bords',
        simple: 'documents',
      }[document.type];

      if (document.type === 'concours') {
        if (updatedData.coverImage && !String(updatedData.coverImage).startsWith('http')) {
          updatedData.coverImage = await uploadImage(updatedData.coverImage, 'concours');
        }

        // Arrêtés
        const { restrictionTelechargement } = updatedData; // ✅ Extraire le flag
        updatedData.arretes = await Promise.all(
          (updatedData.arretes || []).map(async (a) => {
            let docUrl = a.document;
            if (a.document?.uri && !String(a.document).startsWith('http')) {
              docUrl = await uploadDocument(a.document.uri, 'arretes', restrictionTelechargement);
            }
            return {
              id: a.id || safeId(),
              titre: a.titre,
              annee: a.annee,
              document: docUrl || a.url || null, // on garde .document comme champ principal
              url: a.url || null,
            };
          })
        );

        // Documents / Groupes
        // Remplace le PDF si un nouveau fichier a été choisi, en CONSERVANT
        // toutes les autres métadonnées (prixInitial, reductionPourcent,
        // prixFinal, epreuveId...). L'ancienne version reconstruisait l'objet
        // champ par champ et perdait silencieusement les réductions.
        const uploadEpreuve = async (d) => {
          const withId = ensureEpreuveId(d);
          let docUrl = withId.document;
          if (withId.document?.uri && !String(withId.document).startsWith('http')) {
            docUrl = await uploadDocument(withId.document.uri, 'concours', restrictionTelechargement);
          }
          return { ...withId, document: docUrl || withId.url || null };
        };

        if (updatedData.hasGroups) {
          updatedData.groups = await Promise.all(
            (updatedData.groups || []).map(async (g) => ({
              ...g,
              id: g.id || safeId(),
              documents: await Promise.all((g.documents || []).map(uploadEpreuve)),
            }))
          );
          delete updatedData.documents;
        } else {
          delete updatedData.groups;
          updatedData.documents = await Promise.all(
            (updatedData.documents || []).map(uploadEpreuve)
          );
        }
      }

      if (document.type === 'bords') {
        // Image, docs principaux (array), échantillons, feuilletage, docs optionnels, liens
        const { restrictionTelechargement } = updatedData; // ✅ Extraire le flag
        updatedData.image = await uploadImage(updatedData.image, 'bords');

        updatedData.documents = await Promise.all(
          (updatedData.documents || []).map(async (item) => {
            if (typeof item === 'string') return item; // déjà URL
            if (item?.uri) return await uploadDocument(item.uri, 'bords', restrictionTelechargement);
            return item;
          })
        );

        // Upload des documents anglais
        updatedData.documentsAnglais = await Promise.all(
          (updatedData.documentsAnglais || []).map(async (item) => {
            if (typeof item === 'string') return item; // déjà URL
            if (item?.uri) return await uploadDocument(item.uri, 'bords_anglais', restrictionTelechargement);
            return item;
          })
        );

        updatedData.echantillons = await Promise.all(
          (updatedData.echantillons || []).map(async (u) => await uploadImage(u, 'echantillons'))
        );

        if (updatedData.feuilletage?.uri && !String(updatedData.feuilletage).startsWith('http')) {
          updatedData.feuilletage = await uploadDocument(updatedData.feuilletage.uri, 'feuilletage', restrictionTelechargement);
        }

        updatedData.documentsOptionnels = await Promise.all(
          (updatedData.documentsOptionnels || []).map(async (d) => {
            let url = d.document;
            if (d.document?.uri && !String(d.document).startsWith('http')) {
              url = await uploadDocument(d.document.uri, 'bords_optionnels', restrictionTelechargement);
            }
            return {
              id: d.id || safeId(),
              titre: d.titre || '',
              description: d.description || '',
              prix: d.prix || '',
              document: url,
            };
          })
        );

        updatedData.liens = (updatedData.liens || [])
          .filter((l) => (l.url || '').trim())
          .map((l) => ({
            id: l.id || safeId(),
            titre: (l.titre || '').trim(),
            url: (l.url || '').trim(),
            description: (l.description || '').trim(),
          }));
      }

      if (document.type === 'simple') {
        const { restrictionTelechargement } = updatedData; // ✅ Extraire le flag
        if (updatedData.coverImage && !String(updatedData.coverImage).startsWith('http')) {
          updatedData.coverImage = await uploadImage(updatedData.coverImage, 'simple');
        }
        if (updatedData.document?.uri && !String(updatedData.document).startsWith('http')) {
          updatedData.document = await uploadDocument(updatedData.document.uri, 'simple', restrictionTelechargement);
        }

        updatedData.liens = (updatedData.liens || [])
          .filter((l) => (l.url || '').trim())
          .map((l) => ({
            id: l.id || safeId(),
            titre: (l.titre || '').trim(),
            url: (l.url || '').trim(),
            description: (l.description || '').trim(),
          }));
      }

      // Nettoyage
      delete updatedData.id;
      delete updatedData.type;

      await updateDoc(doc(db, collectionName, document.id), updatedData);

      if (Platform.OS === 'web') {
        alert('Document mis à jour avec succès !');
        navigation.goBack();
      } else {
        Alert.alert('Succès', 'Document mis à jour avec succès !');
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error updating document:', error);
      Platform.OS === 'web'
        ? alert('Une erreur est survenue lors de la mise à jour')
        : Alert.alert('Erreur', 'Une erreur est survenue lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------- UI ---------------------------------- */
  const renderHeader = () => (
    <View style={[styles.header, { backgroundColor: theme.primary }]}>
      <TouchableOpacity
        style={[styles.backButton, { backgroundColor: 'rgba(255, 255, 255, 0.2)' }]}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
      </TouchableOpacity>
      <Text style={[styles.headerTitle, { color: '#fff' }]}>Modifier le Document</Text>
    </View>
  );

  /* ------------------------------- CONCOURS UI ------------------------------ */
  const renderConcoursForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Informations du Concours</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage('concours')}>
          {formData.coverImage ? (
            <>
              <Image source={{ uri: formData.coverImage }} style={styles.uploadedImage} />
              <View style={[styles.uploadOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.3)' }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)']} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>
                Modifier l'image de couverture
              </Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Sigle du concours (ex: ENAM)"
          value={formData.sigle}
          onChangeText={(text) => setFormData((p) => ({ ...p, sigle: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Nom complet du concours"
          value={formData.nomComplet}
          onChangeText={(text) => setFormData((p) => ({ ...p, nomComplet: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Type du concours"
          value={formData.typeConcours}
          onChangeText={(text) => setFormData((p) => ({ ...p, typeConcours: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Niveau requis"
          value={formData.niveau}
          onChangeText={(text) => setFormData((p) => ({ ...p, niveau: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <View style={[styles.typeSelector, { backgroundColor: theme.background }]}>
          <TouchableOpacity
            style={[styles.typeOption, formData.langue === 'fr' && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, langue: 'fr' }))}
          >
            <Text style={[styles.typeOptionText, { color: formData.langue === 'fr' ? '#fff' : theme.textSecondary }]}>Français</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, formData.langue === 'en' && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, langue: 'en' }))}
          >
            <Text style={[styles.typeOptionText, { color: formData.langue === 'en' ? '#fff' : theme.textSecondary }]}>Anglais</Text>
          </TouchableOpacity>
        </View>

        <RichTextEditor
          value={formData.description}
          onChange={(text) => setFormData((p) => ({ ...p, description: text }))}
          placeholder="Description du concours"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <View style={[styles.typeSelector, { backgroundColor: theme.background, marginBottom: 20 }]}>
          <TouchableOpacity
            style={[styles.typeOption, !formData.hasGroups && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, hasGroups: false }))}
          >
            <Text style={[styles.typeOptionText, { color: !formData.hasGroups ? '#fff' : theme.textSecondary }]}>Sans Groupes</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, formData.hasGroups && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, hasGroups: true }))}
          >
            <Text style={[styles.typeOptionText, { color: formData.hasGroups ? '#fff' : theme.textSecondary }]}>Avec Groupes</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Arrêtés */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Arrêtés du Concours</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={addArrete}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {(formData.arretes || []).map((a, idx) => (
          <View key={a.id} style={[styles.arreteContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <View style={styles.arreteHeader}>
              <Text style={[styles.arreteTitle, { color: theme.text }]}>Arrêté</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setFormData((p) => ({ ...p, arretes: moveItem(p.arretes, idx, idx - 1) }))}
                  disabled={idx === 0}
                >
                  <MaterialCommunityIcons name="chevron-up" size={22} color={idx === 0 ? theme.textSecondary : theme.text} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setFormData((p) => ({ ...p, arretes: moveItem(p.arretes, idx, idx + 1) }))}
                  disabled={idx === (formData.arretes || []).length - 1}
                >
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={22}
                    color={idx === (formData.arretes || []).length - 1 ? theme.textSecondary : theme.text}
                  />
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeArrete(a.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>
            </View>

            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Titre de l'arrêté"
              value={a.titre}
              onChangeText={(t) => updateArrete(a.id, 'titre', t)}
              placeholderTextColor={theme.textSecondary}
            />
            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Année de l'arrêté"
              value={a.annee?.toString() ?? ''}
              onChangeText={(t) => updateArrete(a.id, 'annee', t)}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />

            <Text style={[styles.optionLabel, { color: theme.text }]}>Option 1: Télécharger un fichier</Text>
            <TouchableOpacity
              style={[styles.documentUpload, { borderColor: theme.border }]}
              onPress={() => handlePickDocumentForArrete(a.id)}
            >
              <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.documentUploadContent}>
                <MaterialCommunityIcons name={a.document ? 'file-check' : 'file-upload'} size={24} color={theme.primary} />
                <Text style={[styles.documentUploadText, { color: theme.text }]}>
                  {a.document ? getFileName(a.document) : "Télécharger l'arrêté (PDF)"}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text style={[styles.optionLabel, { color: theme.text, marginTop: 12 }]}>Option 2: Ajouter un lien</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Lien vers l'arrêté (URL)"
              value={a.url}
              onChangeText={(t) => updateArrete(a.id, 'url', t)}
              placeholderTextColor={theme.textSecondary}
              autoCapitalize="none"
              keyboardType="url"
            />

            {(a.document || a.url) && (
              <View style={[styles.statusIndicator, { backgroundColor: theme.primary + '10' }]}>
                <MaterialCommunityIcons name="check-circle" size={16} color={theme.primary} />
                <Text style={[styles.statusText, { color: theme.primary }]}>
                  {a.document && a.url ? 'Fichier et lien ajoutés' : a.document ? 'Fichier ajouté' : 'Lien ajouté'}
                </Text>
              </View>
            )}
          </View>
        ))}

        {(formData.arretes || []).length === 0 && (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={addArrete}>
            <MaterialCommunityIcons name="gavel" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter le premier arrêté</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Documents / Groupes */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>
          {formData.hasGroups ? 'Groupes de Documents' : 'Documents du Concours'}
        </Text>

        {formData.hasGroups ? (
          <>
            {(formData.groups || []).map((g, gIdx) => (
              <View key={g.id} style={[styles.groupContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
                <View style={[styles.groupHeader, { gap: 6 }]}>
                  <View style={styles.flex1}>
                    <TextInput
                      style={[styles.groupNameInput, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Nom du groupe"
                      value={g.nom}
                      onChangeText={(t) => updateGroup(g.id, 'nom', t)}
                      placeholderTextColor={theme.textSecondary}
                    />
                  </View>

                  <TouchableOpacity
                    style={styles.iconBtn}
                    onPress={() => setFormData((p) => ({ ...p, groups: moveItem(p.groups, gIdx, gIdx - 1) }))}
                    disabled={gIdx === 0}
                  >
                    <MaterialCommunityIcons name="chevron-up" size={22} color={gIdx === 0 ? theme.textSecondary : theme.text} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.iconBtn}
                    onPress={() => setFormData((p) => ({ ...p, groups: moveItem(p.groups, gIdx, gIdx + 1) }))}
                    disabled={gIdx === (formData.groups || []).length - 1}
                  >
                    <MaterialCommunityIcons
                      name="chevron-down"
                      size={22}
                      color={gIdx === (formData.groups || []).length - 1 ? theme.textSecondary : theme.text}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity style={styles.deleteGroupButton} onPress={() => removeGroup(g.id)}>
                    <MaterialCommunityIcons name="delete" size={24} color={theme.danger} />
                  </TouchableOpacity>
                </View>

                {(g.documents || []).map((d, dIdx) => (
                  <View key={`${g.id}-${dIdx}`} style={[styles.documentContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                    <View style={styles.documentHeader}>
                      <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                      <Text style={[styles.documentTitle, { color: theme.text }]}>
                        {d.document ? getFileName(d.document) : d.titre || 'Document'}
                      </Text>

                      <TouchableOpacity
                        style={styles.iconBtn}
                        onPress={() =>
                          setFormData((p) => ({
                            ...p,
                            groups: p.groups.map((gg, i) =>
                              i !== gIdx ? gg : { ...gg, documents: moveItem(gg.documents, dIdx, dIdx - 1) }
                            ),
                          }))
                        }
                        disabled={dIdx === 0}
                      >
                        <MaterialCommunityIcons name="chevron-up" size={22} color={dIdx === 0 ? theme.textSecondary : theme.text} />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.iconBtn}
                        onPress={() =>
                          setFormData((p) => ({
                            ...p,
                            groups: p.groups.map((gg, i) =>
                              i !== gIdx ? gg : { ...gg, documents: moveItem(gg.documents, dIdx, dIdx + 1) }
                            ),
                          }))
                        }
                        disabled={dIdx === (g.documents || []).length - 1}
                      >
                        <MaterialCommunityIcons
                          name="chevron-down"
                          size={22}
                          color={dIdx === (g.documents || []).length - 1 ? theme.textSecondary : theme.text}
                        />
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.deleteDocumentButton} onPress={() => replaceDocumentFileInGroup(g.id, dIdx)}>
                        <MaterialCommunityIcons name="file-replace" size={20} color={theme.primary} />
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.deleteDocumentButton} onPress={() => removeDocumentFromGroup(g.id, dIdx)}>
                        <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                      </TouchableOpacity>
                    </View>

                    <TextInput
                      style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Titre du document"
                      value={d.titre}
                      onChangeText={(t) => updateDocumentInGroup(g.id, dIdx, 'titre', t)}
                      placeholderTextColor={theme.textSecondary}
                    />
                    <TextInput
                      style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Filière"
                      value={d.filiere}
                      onChangeText={(t) => updateDocumentInGroup(g.id, dIdx, 'filiere', t)}
                      placeholderTextColor={theme.textSecondary}
                    />

                    <View style={styles.row}>
                      <View style={styles.flex1}>
                        <TextInput
                          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                          placeholder="Année"
                          value={d.annee?.toString() || ''}
                          onChangeText={(t) => updateDocumentInGroup(g.id, dIdx, 'annee', t)}
                          keyboardType="numeric"
                          placeholderTextColor={theme.textSecondary}
                        />
                      </View>
                      <View style={[styles.flex1, styles.ml2]}>
                        <TextInput
                          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                          placeholder="Pages"
                          value={d.nombrePages?.toString() || ''}
                          onChangeText={(t) => updateDocumentInGroup(g.id, dIdx, 'nombrePages', t)}
                          keyboardType="numeric"
                          placeholderTextColor={theme.textSecondary}
                        />
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.flex1}>
                        <TextInput
                          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                          placeholder="Prix (FCFA)"
                          value={d.prix?.toString() || ''}
                          onChangeText={(t) => updateDocumentInGroup(g.id, dIdx, 'prix', t)}
                          keyboardType="numeric"
                          placeholderTextColor={theme.textSecondary}
                          editable={!d.gratuit}
                        />
                      </View>
                      <View style={[styles.flex1, styles.ml2]}>
                        <TouchableOpacity
                          style={[
                            styles.gratuitButton,
                            { backgroundColor: theme.background, borderColor: theme.border },
                            d.gratuit && { backgroundColor: theme.primary, borderColor: theme.primary },
                          ]}
                          onPress={() => updateDocumentInGroup(g.id, dIdx, 'gratuit', !d.gratuit)}
                        >
                          <Text style={[styles.gratuitButtonText, { color: d.gratuit ? '#fff' : theme.text }]}>Gratuit</Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Section Réduction pour document de concours */}
                    {!d.gratuit && (
                      <View style={{ marginTop: 12, padding: 12, backgroundColor: theme.surface, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                          <MaterialCommunityIcons name="sale" size={18} color={theme.primary} />
                          <Text style={{ fontSize: 14, fontWeight: '600', color: theme.text, marginLeft: 6 }}>
                            Réduction (Optionnel)
                          </Text>
                        </View>

                        <TextInput
                          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border, fontSize: 14 }]}
                          placeholder="% de réduction (ex: 20)"
                          value={d.reductionPourcent?.toString() || ''}
                          onChangeText={(text) => {
                            const prixInitialValue = d.prixInitial || d.prix;
                            const result = calculateReduction(prixInitialValue, text);
                            updateDocumentInGroup(g.id, dIdx, 'reductionPourcent', text);
                            updateDocumentInGroup(g.id, dIdx, 'prixInitial', result.prixInitial);
                            updateDocumentInGroup(g.id, dIdx, 'prixFinal', result.prixFinal);
                          }}
                          keyboardType="numeric"
                          placeholderTextColor={theme.textSecondary}
                        />

                        {parseFloat(d.reductionPourcent || 0) > 0 && (
                          <View style={{ marginTop: 8 }}>
                            <View style={styles.row}>
                              <View style={styles.flex1}>
                                <Text style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>Prix initial</Text>
                                <TextInput
                                  style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border, fontSize: 14 }]}
                                  value={d.prixInitial?.toString() || ''}
                                  onChangeText={(text) => {
                                    const result = calculateReduction(text, d.reductionPourcent);
                                    updateDocumentInGroup(g.id, dIdx, 'prixInitial', text);
                                    updateDocumentInGroup(g.id, dIdx, 'prixFinal', result.prixFinal);
                                  }}
                                  keyboardType="numeric"
                                  placeholderTextColor={theme.textSecondary}
                                />
                              </View>
                              <View style={[styles.flex1, styles.ml2]}>
                                <Text style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>Prix final</Text>
                                <TextInput
                                  style={[styles.input, { backgroundColor: '#FF444410', color: '#FF4444', borderColor: '#FF4444', fontWeight: 'bold', fontSize: 14 }]}
                                  value={d.prixFinal?.toString() || ''}
                                  editable={false}
                                />
                              </View>
                            </View>
                            <Text style={{ fontSize: 10, color: theme.primary, marginTop: 4 }}>
                              -{d.reductionPourcent}% = {d.prixFinal} FCFA
                            </Text>
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                ))}

                <TouchableOpacity style={styles.addButton} onPress={() => addDocumentToGroup(g.id)}>
                  <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.addButtonContent}>
                    <MaterialCommunityIcons name="file-plus" size={24} color={theme.primary} />
                    <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un document au groupe</Text>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            ))}

            <TouchableOpacity style={[styles.addButton, { marginTop: 20 }]} onPress={addGroup}>
              <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.addButtonContent}>
                <MaterialCommunityIcons name="account-group-outline" size={24} color={theme.primary} />
                <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un nouveau groupe</Text>
              </LinearGradient>
            </TouchableOpacity>
          </>
        ) : (
          <>
            {(formData.documents || []).map((d, idx) => (
              <View key={`doc-${idx}`} style={[styles.documentContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                <View style={styles.documentHeader}>
                  <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                  <Text style={[styles.documentTitle, { color: theme.text }]}>{d.document ? getFileName(d.document) : d.titre || 'Document'}</Text>

                  <TouchableOpacity
                    style={styles.iconBtn}
                    onPress={() => setFormData((p) => ({ ...p, documents: moveItem(p.documents, idx, idx - 1) }))}
                    disabled={idx === 0}
                  >
                    <MaterialCommunityIcons name="chevron-up" size={22} color={idx === 0 ? theme.textSecondary : theme.text} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.iconBtn}
                    onPress={() => setFormData((p) => ({ ...p, documents: moveItem(p.documents, idx, idx + 1) }))}
                    disabled={idx === (formData.documents || []).length - 1}
                  >
                    <MaterialCommunityIcons
                      name="chevron-down"
                      size={22}
                      color={idx === (formData.documents || []).length - 1 ? theme.textSecondary : theme.text}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.deleteDocumentButton}
                    onPress={() => removeDocumentFromFlatList(idx)}
                  >
                    <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                  </TouchableOpacity>
                </View>

                <TextInput
                  style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                  placeholder="Titre du document"
                  value={d.titre}
                  onChangeText={(t) =>
                    setFormData((p) => ({
                      ...p,
                      documents: (p.documents || []).map((dd, i) => (i === idx ? { ...dd, titre: t } : dd)),
                    }))
                  }
                  placeholderTextColor={theme.textSecondary}
                />
                <TextInput
                  style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                  placeholder="Filière"
                  value={d.filiere}
                  onChangeText={(t) =>
                    setFormData((p) => ({
                      ...p,
                      documents: (p.documents || []).map((dd, i) => (i === idx ? { ...dd, filiere: t } : dd)),
                    }))
                  }
                  placeholderTextColor={theme.textSecondary}
                />

                <View style={styles.row}>
                  <View style={styles.flex1}>
                    <TextInput
                      style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Année"
                      value={d.annee?.toString() || ''}
                      onChangeText={(t) =>
                        setFormData((p) => ({
                          ...p,
                          documents: (p.documents || []).map((dd, i) => (i === idx ? { ...dd, annee: t } : dd)),
                        }))
                      }
                      keyboardType="numeric"
                      placeholderTextColor={theme.textSecondary}
                    />
                  </View>
                  <View style={[styles.flex1, styles.ml2]}>
                    <TextInput
                      style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Pages"
                      value={d.nombrePages?.toString() || ''}
                      onChangeText={(t) =>
                        setFormData((p) => ({
                          ...p,
                          documents: (p.documents || []).map((dd, i) => (i === idx ? { ...dd, nombrePages: t } : dd)),
                        }))
                      }
                      keyboardType="numeric"
                      placeholderTextColor={theme.textSecondary}
                    />
                  </View>
                </View>

                <View style={styles.row}>
                  <View style={styles.flex1}>
                    <TextInput
                      style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                      placeholder="Prix (FCFA)"
                      value={d.prix?.toString() || ''}
                      onChangeText={(t) =>
                        setFormData((p) => ({
                          ...p,
                          documents: (p.documents || []).map((dd, i) => (i === idx ? { ...dd, prix: t } : dd)),
                        }))
                      }
                      keyboardType="numeric"
                      placeholderTextColor={theme.textSecondary}
                      editable={!d.gratuit}
                    />
                  </View>
                  <View style={[styles.flex1, styles.ml2]}>
                    <TouchableOpacity
                      style={[
                        styles.gratuitButton,
                        { backgroundColor: theme.background, borderColor: theme.border },
                        d.gratuit && { backgroundColor: theme.primary, borderColor: theme.primary },
                      ]}
                      onPress={() =>
                        setFormData((p) => ({
                          ...p,
                          documents: (p.documents || []).map((dd, i) =>
                            i === idx ? { ...dd, gratuit: !dd.gratuit, prix: dd.gratuit ? '' : '0' } : dd
                          ),
                        }))
                      }
                    >
                      <Text style={[styles.gratuitButtonText, { color: d.gratuit ? '#fff' : theme.text }]}>Gratuit</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Section Réduction pour document de concours non groupé */}
                {!d.gratuit && (
                  <View style={{ marginTop: 12, padding: 12, backgroundColor: theme.background, borderRadius: 8, borderWidth: 1, borderColor: theme.border }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                      <MaterialCommunityIcons name="sale" size={18} color={theme.primary} />
                      <Text style={{ fontSize: 14, fontWeight: '600', color: theme.text, marginLeft: 6 }}>
                        Réduction (Optionnel)
                      </Text>
                    </View>

                    <TextInput
                      style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border, fontSize: 14 }]}
                      placeholder="% de réduction (ex: 20)"
                      value={d.reductionPourcent?.toString() || ''}
                      onChangeText={(text) => {
                        const prixInitialValue = d.prixInitial || d.prix;
                        const result = calculateReduction(prixInitialValue, text);
                        setFormData((p) => ({
                          ...p,
                          documents: (p.documents || []).map((dd, i) =>
                            i === idx
                              ? {
                                  ...dd,
                                  reductionPourcent: text,
                                  prixInitial: result.prixInitial,
                                  prixFinal: result.prixFinal,
                                }
                              : dd
                          ),
                        }));
                      }}
                      keyboardType="numeric"
                      placeholderTextColor={theme.textSecondary}
                    />

                    {parseFloat(d.reductionPourcent || 0) > 0 && (
                      <View style={{ marginTop: 8 }}>
                        <View style={styles.row}>
                          <View style={styles.flex1}>
                            <Text style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>Prix initial</Text>
                            <TextInput
                              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border, fontSize: 14 }]}
                              value={d.prixInitial?.toString() || ''}
                              onChangeText={(text) => {
                                const result = calculateReduction(text, d.reductionPourcent);
                                setFormData((p) => ({
                                  ...p,
                                  documents: (p.documents || []).map((dd, i) =>
                                    i === idx
                                      ? { ...dd, prixInitial: text, prixFinal: result.prixFinal }
                                      : dd
                                  ),
                                }));
                              }}
                              keyboardType="numeric"
                              placeholderTextColor={theme.textSecondary}
                            />
                          </View>
                          <View style={[styles.flex1, styles.ml2]}>
                            <Text style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>Prix final</Text>
                            <TextInput
                              style={[styles.input, { backgroundColor: '#FF444410', color: '#FF4444', borderColor: '#FF4444', fontWeight: 'bold', fontSize: 14 }]}
                              value={d.prixFinal?.toString() || ''}
                              editable={false}
                            />
                          </View>
                        </View>
                        <Text style={{ fontSize: 10, color: theme.primary, marginTop: 4 }}>
                          -{d.reductionPourcent}% = {d.prixFinal} FCFA
                        </Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
            ))}

            <TouchableOpacity style={styles.addButton} onPress={handlePickConcoursDocument}>
              <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.addButtonContent}>
                <MaterialCommunityIcons name="file-plus" size={24} color={theme.primary} />
                <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un document</Text>
              </LinearGradient>
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Restriction de téléchargement — SUSPENDUE */}
      {RESTRICTION_FEATURE_ENABLED && (
      <View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.restrictionInfo}>
          <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
          <View style={styles.restrictionTextContainer}>
            <Text style={[styles.restrictionTitle, { color: theme.text }]}>
              Restriction de téléchargement
            </Text>
            <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
              Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
            </Text>
          </View>
        </View>
        <input
          type="checkbox"
          checked={formData.restrictionTelechargement}
          onChange={(e) => setFormData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
          style={{ width: 48, height: 24, cursor: "pointer" }}
        />
      </View>
      )}

      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: '#fff' }]}>Mettre à jour</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  /* --------------------------------- BORDS UI ------------------------------- */
  const renderBordsForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Informations du Bord</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage('bords', false)}>
          {formData.image ? (
            <>
              <Image source={{ uri: formData.image }} style={styles.uploadedImage} />
              <View style={[styles.uploadOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.3)' }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)']} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>
                Modifier l'image de couverture
              </Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Titre du bord"
          value={formData.titre}
          onChangeText={(text) => setFormData((p) => ({ ...p, titre: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <RichTextEditor
          value={formData.description}
          onChange={(text) => setFormData((p) => ({ ...p, description: text }))}
          placeholder="Description détaillée"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <View style={[styles.typeSelector, { backgroundColor: theme.background }]}>
          <TouchableOpacity
            style={[styles.typeOption, formData.langue === 'fr' && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, langue: 'fr' }))}
          >
            <Text style={[styles.typeOptionText, { color: formData.langue === 'fr' ? '#fff' : theme.textSecondary }]}>Français</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, formData.langue === 'en' && { backgroundColor: theme.primary }]}
            onPress={() => setFormData((p) => ({ ...p, langue: 'en' }))}
          >
            <Text style={[styles.typeOptionText, { color: formData.langue === 'en' ? '#fff' : theme.textSecondary }]}>Anglais</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.row}>
          <View className="flex-1" style={styles.flex1}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Prix (FCFA)"
              value={formData.prix}
              onChangeText={(text) => setFormData((p) => ({ ...p, prix: text }))}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>
          <View style={[styles.flex1, styles.ml2]}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Nombre de pages"
              value={formData.nombrePages}
              onChangeText={(text) => setFormData((p) => ({ ...p, nombrePages: text }))}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>
        </View>

        {/* Section Réduction */}
        <View style={{ marginTop: 20, padding: 16, backgroundColor: theme.background, borderRadius: 12, borderWidth: 1, borderColor: theme.border }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
            <MaterialCommunityIcons name="sale" size={24} color={theme.primary} />
            <Text style={{ fontSize: 16, fontWeight: '600', color: theme.text, marginLeft: 8 }}>
              Réduction (Optionnel)
            </Text>
          </View>

          <TextInput
            style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
            placeholder="Pourcentage de réduction (ex: 20)"
            value={formData.reductionPourcent}
            onChangeText={(text) => {
              const prixInitialValue = formData.prixInitial || formData.prix;
              const result = calculateReduction(prixInitialValue, text);
              setFormData((p) => ({
                ...p,
                reductionPourcent: text,
                prixInitial: result.prixInitial,
                prixFinal: result.prixFinal
              }));
            }}
            keyboardType="numeric"
            placeholderTextColor={theme.textSecondary}
          />

          {parseFloat(formData.reductionPourcent || 0) > 0 && (
            <View style={{ marginTop: 12 }}>
              <View style={styles.row}>
                <View style={styles.flex1}>
                  <Text style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 4 }}>Prix initial</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                    placeholder="Prix initial"
                    value={formData.prixInitial}
                    onChangeText={(text) => {
                      const result = calculateReduction(text, formData.reductionPourcent);
                      setFormData((p) => ({ ...p, prixInitial: text, prixFinal: result.prixFinal }));
                    }}
                    keyboardType="numeric"
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
                <View style={[styles.flex1, styles.ml2]}>
                  <Text style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 4 }}>Prix final</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: '#FF444410', color: '#FF4444', borderColor: '#FF4444', fontWeight: 'bold' }]}
                    placeholder="Prix final"
                    value={formData.prixFinal}
                    editable={false}
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, padding: 8, backgroundColor: '#4C6FFF10', borderRadius: 8 }}>
                <MaterialCommunityIcons name="information" size={16} color={theme.primary} />
                <Text style={{ fontSize: 12, color: theme.primary, marginLeft: 6, flex: 1 }}>
                  Le prix final est calculé automatiquement : {formData.prixInitial} FCFA - {formData.reductionPourcent}% = {formData.prixFinal} FCFA
                </Text>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Documents principaux (liste) */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document(s) Principal(aux)</Text>
        <TouchableOpacity style={[styles.documentUpload, { borderColor: theme.border }]} onPress={handlePickBordsDocument}>
          <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name="file-upload" size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>Ajouter un PDF principal</Text>
          </LinearGradient>
        </TouchableOpacity>

        {(formData.documents || []).map((docItem, i) => (
          <View key={`main-${i}`} style={[styles.documentItem, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
            <Text style={[styles.documentName, { color: theme.text }]}>{getFileName(docItem)}</Text>
            <TouchableOpacity onPress={() => removeBordDocument(i)}>
              <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      {/* Version Anglaise du Bord */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Version Anglaise (Optionnel)</Text>
        <Text style={[styles.formattingHint, { color: theme.textSecondary, marginBottom: 12 }]}>
          Ajoutez ou modifiez la version anglaise du bord. L'achat donne accès aux deux versions.
        </Text>
        <TouchableOpacity
          style={[styles.documentUpload, { borderColor: theme.border }]}
          onPress={async () => {
            try {
              const file = await pickPDF();
              if (!file) return;
              setFormData((p) => ({ ...p, documentsAnglais: [...(p.documentsAnglais || []), file] }));
            } catch { /* ignore */ }
          }}
        >
          <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name="translate" size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>Ajouter la version anglaise (PDF)</Text>
          </LinearGradient>
        </TouchableOpacity>

        {(formData.documentsAnglais || []).map((docItem, i) => (
          <View key={`ang-${i}`} style={[styles.documentItem, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
            <Text style={[styles.documentName, { color: theme.text }]}>{getFileName(docItem)}</Text>
            <TouchableOpacity onPress={() => setFormData((p) => ({ ...p, documentsAnglais: (p.documentsAnglais || []).filter((_, x) => x !== i) }))}>
              <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      {/* Documents additionnels */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Documents additionnels</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={pickDocumentOptionnel}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {(formData.documentsOptionnels || []).length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={pickDocumentOptionnel}>
            <MaterialCommunityIcons name="file-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un document additionnel</Text>
          </TouchableOpacity>
        ) : (
          (formData.documentsOptionnels || []).map((doc) => (
            <View key={doc.id} style={[styles.documentContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.documentHeader}>
                <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                <Text style={[styles.documentTitle, { color: theme.text }]}>{getFileName(doc.document) || 'Document additionnel'}</Text>
                <TouchableOpacity style={styles.deleteDocumentButton} onPress={() => removeDocumentOptionnel(doc.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du document"
                value={doc.titre}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, 'titre', t)}
                placeholderTextColor={theme.textSecondary}
              />

              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du document"
                value={doc.description}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, 'description', t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />

              {/* Prix optionnel : vide = document inclus / gratuit */}
              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Prix (FCFA) — laisser vide pour inclus/gratuit"
                value={doc.prix || ''}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, 'prix', t.replace(/[^0-9]/g, ''))}
                keyboardType="numeric"
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      {/* Liens Utiles */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Liens Utiles</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={() => addLien('bords')}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {(formData.liens || []).length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={() => addLien('bords')}>
            <MaterialCommunityIcons name="link-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un lien utile</Text>
          </TouchableOpacity>
        ) : (
          (formData.liens || []).map((lien) => (
            <View key={lien.id} style={[styles.lienContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.lienHeader}>
                <MaterialCommunityIcons name="link" size={24} color={theme.primary} />
                <Text style={[styles.lienTitle, { color: theme.text }]}>Lien</Text>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeLien('bords', lien.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du lien"
                value={lien.titre}
                onChangeText={(t) => updateLien('bords', lien.id, 'titre', t)}
                placeholderTextColor={theme.textSecondary}
              />
              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="URL du lien"
                value={lien.url}
                onChangeText={(t) => updateLien('bords', lien.id, 'url', t)}
                placeholderTextColor={theme.textSecondary}
                autoCapitalize="none"
                keyboardType="url"
              />
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du lien"
                value={lien.description}
                onChangeText={(t) => updateLien('bords', lien.id, 'description', t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      {/* Échantillons */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Échantillons</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.echantillonsContainer}>
          {(formData.echantillons || []).map((uri, i) => (
            <View key={`${uri}-${i}`} style={styles.echantillonWrapper}>
              <Image source={{ uri }} style={styles.echantillonImage} resizeMode="cover" />
              <TouchableOpacity
                style={[styles.deleteEchantillonButton, { backgroundColor: 'rgba(255,255,255,.9)' }]}
                onPress={() => setFormData((p) => ({ ...p, echantillons: (p.echantillons || []).filter((_, x) => x !== i) }))}
              >
                <MaterialCommunityIcons name="close-circle" size={24} color={theme.danger} />
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity style={styles.addEchantillonButton} onPress={() => pickImage('bords', true)}>
            <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.addEchantillonContent}>
              <MaterialCommunityIcons name="image-plus" size={32} color={theme.primary} />
            </LinearGradient>
          </TouchableOpacity>
        </ScrollView>
      </View>

      {/* Feuilletage */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document de Feuilletage (Optionnel)</Text>
        <Text style={[styles.description, { color: theme.textSecondary, marginBottom: 12 }]}>
          Ajoutez un document PDF permettant aux acheteurs de feuilleter quelques pages avant l'achat
        </Text>

        <TouchableOpacity style={[styles.documentUpload, { borderColor: theme.border }]} onPress={handlePickFeuilletage}>
          <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name={formData.feuilletage ? 'file-check' : 'file-upload'} size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>
              {formData.feuilletage ? getFileName(formData.feuilletage) : 'Ajouter un document de feuilletage (PDF)'}
            </Text>
          </LinearGradient>
        </TouchableOpacity>

        {formData.feuilletage && (
          <TouchableOpacity
            style={[styles.removeFeuilletage, { borderColor: theme.border, marginTop: 8 }]}
            onPress={() => setFormData((p) => ({ ...p, feuilletage: null }))}
          >
            <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            <Text style={[styles.removeFeuilletageText, { color: theme.danger, marginLeft: 8 }]}>
              Supprimer le document de feuilletage
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Restriction de téléchargement — SUSPENDUE */}
      {RESTRICTION_FEATURE_ENABLED && (
      <View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.restrictionInfo}>
          <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
          <View style={styles.restrictionTextContainer}>
            <Text style={[styles.restrictionTitle, { color: theme.text }]}>
              Restriction de téléchargement
            </Text>
            <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
              Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
            </Text>
          </View>
        </View>
        <input
          type="checkbox"
          checked={formData.restrictionTelechargement}
          onChange={(e) => setFormData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
          style={{ width: 48, height: 24, cursor: "pointer" }}
        />
      </View>
      )}

      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: '#fff' }]}>Mettre à jour</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  /* --------------------------------- SIMPLE UI ------------------------------ */
  const renderSimpleForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document Simple</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage('simple')}>
          {formData.coverImage ? (
            <>
              <Image source={{ uri: formData.coverImage }} style={styles.uploadedImage} />
              <View style={[styles.uploadOverlay, { backgroundColor: 'rgba(0,0,0,0.3)' }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)']} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>Ajouter une image de couverture</Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Titre du document"
          value={formData.titre}
          onChangeText={(text) => setFormData((p) => ({ ...p, titre: text }))}
          placeholderTextColor={theme.textSecondary}
        />

        <RichTextEditor
          value={formData.description}
          onChange={(text) => setFormData((p) => ({ ...p, description: text }))}
          placeholder="Description du document"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <TouchableOpacity
          style={[styles.documentUpload, formData.document && styles.documentUploaded, { borderColor: theme.border }]}
          onPress={async () => {
            const file = await pickPDF();
            if (!file) return;
            setFormData((p) => ({ ...p, document: file }));
          }}
        >
          <LinearGradient colors={[theme.primary + '20', theme.primary + '10']} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name={formData.document ? 'file-check' : 'file-upload'} size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>
              {formData.document ? getFileName(formData.document) : 'Télécharger le document (PDF)'}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>

      {/* Liens Utiles */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Liens Utiles</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={() => addLien('simple')}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {(formData.liens || []).length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={() => addLien('simple')}>
            <MaterialCommunityIcons name="link-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un lien utile</Text>
          </TouchableOpacity>
        ) : (
          (formData.liens || []).map((lien) => (
            <View key={lien.id} style={[styles.lienContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.lienHeader}>
                <MaterialCommunityIcons name="link" size={24} color={theme.primary} />
                <Text style={[styles.lienTitle, { color: theme.text }]}>Lien</Text>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeLien('simple', lien.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du lien"
                value={lien.titre}
                onChangeText={(t) => updateLien('simple', lien.id, 'titre', t)}
                placeholderTextColor={theme.textSecondary}
              />

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="URL du lien"
                value={lien.url}
                onChangeText={(t) => updateLien('simple', lien.id, 'url', t)}
                placeholderTextColor={theme.textSecondary}
                autoCapitalize="none"
                keyboardType="url"
              />

              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du lien"
                value={lien.description}
                onChangeText={(t) => updateLien('simple', lien.id, 'description', t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      {/* Restriction de téléchargement — SUSPENDUE */}
      {RESTRICTION_FEATURE_ENABLED && (
      <View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border, marginTop: 16 }]}>
        <View style={styles.restrictionInfo}>
          <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
          <View style={styles.restrictionTextContainer}>
            <Text style={[styles.restrictionTitle, { color: theme.text }]}>
              Restriction de téléchargement
            </Text>
            <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
              Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
            </Text>
          </View>
        </View>
        <input
          type="checkbox"
          checked={formData.restrictionTelechargement}
          onChange={(e) => setFormData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
          style={{ width: 48, height: 24, cursor: "pointer" }}
        />
      </View>
      )}

      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: '#fff' }]}>Mettre à jour</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  const renderForm = () => {
    if (!document || !document.type) {
      return null;
    }

    switch (document.type) {
      case 'concours':
        return renderConcoursForm();
      case 'bords':
        return renderBordsForm();
      case 'simple':
        return renderSimpleForm();
      default:
        return null;
    }
  };

  // Vérifier si le document est valide
  if (!document || !document.type) {
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
        <StatusBar barStyle={theme.statusBar} backgroundColor={theme.background} />
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
          <MaterialCommunityIcons name="alert-circle" size={64} color={theme.danger || '#F44336'} />
          <Text style={{ fontSize: 18, fontWeight: 'bold', color: theme.text, marginTop: 16, marginBottom: 8 }}>
            Erreur
          </Text>
          <Text style={{ fontSize: 14, color: theme.textSecondary, textAlign: 'center', marginBottom: 24 }}>
            Document invalide ou non trouvé
          </Text>
          <TouchableOpacity
            style={{
              backgroundColor: theme.primary,
              paddingHorizontal: 24,
              paddingVertical: 12,
              borderRadius: 12,
            }}
            onPress={() => navigation.goBack()}
          >
            <Text style={{ color: '#fff', fontSize: 16, fontWeight: '600' }}>
              Retour
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Sur le web (dashboard), on enveloppe dans AdminLayout. Sur mobile, layout d'origine.
  if (isWeb) {
    return (
      <AdminLayout
        title="Modifier le document"
        subtitle={document.titre || document.sigle || document.nomComplet || 'Édition'}
      >
        {renderForm()}
      </AdminLayout>
    );
  }

  return (
    <View style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      <ScrollView contentContainerStyle={styles.scrollContent} style={[styles.scrollView, { backgroundColor: theme.background }]}>
        {renderHeader()}
        {renderForm()}
      </ScrollView>
      <View style={[styles.container, { backgroundColor: theme.background, flex: 1 }]} />
    </View>
  );
}

/* ---------------------------------- styles --------------------------------- */
const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingBottom: 110 },

  header: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  backButton: {
    width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginRight: 16,
  },
  headerTitle: { fontSize: 20, fontWeight: 'bold' },

  formContainer: { padding: 20 },
  section: { marginBottom: 20, borderRadius: 20, padding: 20, borderWidth: 1 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 16 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  addSmallButton: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },

  imageUploadContainer: { height: 200, borderRadius: 20, overflow: 'hidden', marginBottom: 20 },
  uploadedImage: { width: '100%', height: '100%', resizeMode: 'cover' },
  uploadOverlay: { ...StyleSheet.absoluteFillObject, justifyContent: 'center', alignItems: 'center' },
  uploadPlaceholder: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  uploadText: { marginTop: 10, fontSize: 14 },

  input: { borderRadius: 12, padding: 16, fontSize: 16, marginBottom: 16, borderWidth: 1, width: '100%' },
  textArea: { height: 120, textAlignVertical: 'top' },

  typeSelector: { flexDirection: 'row', borderRadius: 12, padding: 4, marginBottom: 16 },
  typeOption: { flex: 1, paddingVertical: 12, alignItems: 'center', borderRadius: 8 },
  typeOptionText: { fontSize: 15, fontWeight: '600' },

  documentUpload: { marginTop: 8 },
  documentUploadContent: { flexDirection: 'row', alignItems: 'center', padding: 16, borderRadius: 12, borderWidth: 1, borderStyle: 'dashed' },
  documentUploaded: { borderStyle: 'solid' },
  documentUploadText: { marginLeft: 12, fontSize: 14, flex: 1 },

  documentContainer: { borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1 },
  documentHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  documentTitle: { flex: 1, marginLeft: 12, fontSize: 14 },
  deleteDocumentButton: { padding: 8 },

  documentItem: { flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 12, marginTop: 8, borderWidth: 1 },
  documentName: { flex: 1, marginLeft: 12, fontSize: 14 },

  row: { flexDirection: 'row', alignItems: 'center' },
  flex1: { flex: 1 },
  ml2: { marginLeft: 8 },

  gratuitButton: { borderRadius: 12, padding: 16, alignItems: 'center', borderWidth: 1 },
  gratuitButtonText: { fontSize: 16, fontWeight: '600' },

  arreteContainer: { borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1 },
  arreteHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  arreteTitle: { fontSize: 16, fontWeight: '600' },
  deleteButton: { padding: 4 },
  optionLabel: { fontSize: 14, fontWeight: '500', marginBottom: 8, marginTop: 4 },
  statusIndicator: { flexDirection: 'row', alignItems: 'center', padding: 8, borderRadius: 8, marginTop: 8 },
  statusText: { fontSize: 12, fontWeight: '500', marginLeft: 4 },
  emptyStateButton: { alignItems: 'center', justifyContent: 'center', padding: 32, borderRadius: 12, borderWidth: 2, borderStyle: 'dashed' },
  emptyStateText: { marginTop: 8, fontSize: 14, fontWeight: '500' },

  groupContainer: { marginBottom: 20, borderRadius: 12, borderWidth: 1, padding: 16 },
  groupHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  groupNameInput: { fontSize: 18, fontWeight: 'bold', padding: 8, borderRadius: 8, borderWidth: 1, width: '100%' },
  deleteGroupButton: { marginLeft: 12, padding: 8 },

  echantillonsContainer: { flexDirection: 'row' },
  echantillonWrapper: { marginRight: 12, position: 'relative' },
  echantillonImage: { width: 120, height: 120, borderRadius: 12 },
  deleteEchantillonButton: { position: 'absolute', top: 8, right: 8, borderRadius: 12 },
  addEchantillonButton: { width: 120, height: 120, borderRadius: 12, overflow: 'hidden' },
  addEchantillonContent: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  submitButton: { marginVertical: 30 },
  submitButtonDisabled: { opacity: 0.5 },
  submitButtonContent: { padding: 18, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  submitText: { fontSize: 18, fontWeight: 'bold' },

  description: { fontSize: 14, lineHeight: 20, opacity: 0.8 },
  removeFeuilletage: { flexDirection: 'row', alignItems: 'center', padding: 8, borderRadius: 8, borderWidth: 1, alignSelf: 'flex-start' },
  removeFeuilletageText: { fontSize: 14, fontWeight: '500' },

  // boutons ↑/↓
  iconBtn: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, marginHorizontal: 2, backgroundColor: 'rgba(0,0,0,0.04)' },

  // RTE
  formattingToolbar: { flexDirection: 'row', padding: 8, borderRadius: 8, marginBottom: 8, borderWidth: 1, gap: 8 },
  formatButton: { padding: 8, borderRadius: 6, backgroundColor: 'rgba(0,0,0,.05)' },
  formattingHint: { fontSize: 12, marginTop: 4, fontStyle: 'italic' },

  // Liens
  lienContainer: { borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1 },
  lienHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  lienTitle: { flex: 1, marginLeft: 12, fontSize: 16, fontWeight: '600' },
  addButtonText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: '600',
  },
  addButton: { marginTop: 16, cursor: "pointer" },
  addButtonContent: { flexDirection: "row", alignItems: "center", justifyContent: "center", padding: 16, borderRadius: 12 },

  // Restriction de téléchargement
  restrictionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 16,
  },
  restrictionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  restrictionTextContainer: {
    flex: 1,
    marginLeft: 12,
  },
  restrictionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  restrictionDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
});
